import { useDoc } from "../hooks/useDoc";
import { n } from "../lib/format";

/**
 * ÜRÜN STOK VE TESLİM SÜRESİ BLOĞU.
 *
 * ── NİÇİN VAR ──────────────────────────────────────────────────────────────
 * İş kuralı: her ürün sipariş üzerine üretilebilir (10 iş günü); bazı ürünler
 * ise STOĞA üretilir ve stoktan çıktığı için 1 iş gününde gönderilir.
 *
 * Bu ayrım sistemde hiçbir yerde tutulmuyordu — ne eski Balder'de ne yenide.
 * Ekip "o ürüne stok siparişi açtık mı" diye hatırlayarak takip ediyordu.
 * Artık ürünün kendi alanı: `stockPolicy` + `leadTimeDays`.
 *
 * ── STOK SAYISI NEREDEN GELİYOR ────────────────────────────────────────────
 * `inventory/product_{id}` dokümanından. O sayı stok siparişi tamamlandığında
 * artar, sipariş kargoya verildiğinde azalır. Daha önce yalnızca ARTIYORDU;
 * bu yüzden eldeki 3 kayıt da gerçeği yansıtmıyordu.
 */

interface ProductStockFields {
  stockPolicy?: "madeToOrder" | "madeToStock";
  leadTimeDays?: number | null;
  /** `"shopify"` artık tek gerçek kaynak; eski değerler geçmiş kayıtlarda kalır. */
  stockPolicySource?: "history" | "manual" | "shopify";
  stockPolicyEvidence?: { stockOrderCount?: number; totalQuantity?: number } | null;
}

interface InventoryDoc {
  volume?: number;
  minVolume?: number;
  updatedAt?: { toDate: () => Date } | null;
}

interface DeliverySettings {
  leadTimeMadeToOrder?: number;
  leadTimeMadeToStock?: number;
}

export function StockPanel({
  productId,
  product,
}: {
  productId: string;
  product: ProductStockFields;
}) {
  const inv = useDoc<InventoryDoc>(`inventory/product_${productId}`);
  const settings = useDoc<DeliverySettings>("settings/delivery");

  const policy = product.stockPolicy ?? "madeToOrder";
  const isStock = policy === "madeToStock";

  const defaultLead = isStock
    ? (settings.data?.leadTimeMadeToStock ?? 1)
    : (settings.data?.leadTimeMadeToOrder ?? 10);
  const lead = product.leadTimeDays ?? defaultLead;

  const onHand = Number(inv.data?.volume ?? 0);
  // Stok kaydı HİÇ YOKSA "0 adet" demek yanlış olur: ölçülmemiş ile sıfır aynı
  // şey değildir. Bu ayrım stoğa üretilen üründe karar değiştirir.
  const measured = inv.data !== null && inv.data !== undefined;

  /**
   * ★ ELLE DEĞİŞTİRME KALDIRILDI (iş sahibi kararı: "Shopify her zaman daha
   * günceldir"). Politika artık Shopify'daki `ayni-gun-kargo` etiketinden
   * türetiliyor ve her ürün webhook'unda yeniden yazılıyor
   * (`shopify/mappers.ts` → `stockPolicyFromTags`).
   *
   * Buton bırakılsaydı ofis değeri değiştirir, bir sonraki webhook sessizce
   * geri alırdı — "ekran yazıyor, ayna eziyor" hatası. Değiştirmenin doğru
   * yeri artık Shopify ürün etiketleri.
   */

  return (
    <section className="card card-pad">
      <header className="mb-3 flex flex-wrap items-center justify-between gap-2">
        <h3 className="text-base font-semibold text-gray-900">Stok ve Teslim Süresi</h3>
        <span
          className={`inline-flex items-center rounded-md px-2 py-0.5 text-xs font-medium ring-1 ring-inset ${
            isStock
              ? "bg-green-100 text-green-800 ring-green-300"
              : "bg-gray-100 text-gray-700 ring-gray-300"
          }`}
        >
          {isStock ? "Hızlı gönderim" : "Sipariş üzerine üretim"}
        </span>
      </header>

      <dl className="grid grid-cols-1 gap-4 sm:grid-cols-3">
        <div>
          <dt className="text-sm text-gray-600">Elde kalan</dt>
          <dd className="mt-1 text-2xl font-semibold tabular-nums text-gray-900">
            {!isStock ? (
              <span className="text-base font-normal text-gray-600">
                stok tutulmuyor
              </span>
            ) : !measured ? (
              <span className="text-base font-normal text-amber-800">henüz ölçülmedi</span>
            ) : (
              <span className={onHand <= 0 ? "text-red-700" : ""}>
                {n(onHand, " adet")}
              </span>
            )}
          </dd>
          {isStock && measured && onHand <= 0 ? (
            <p className="mt-1 text-xs text-amber-800">
              Stok bitti. Sipariş almaya devam edilir — ancak bu ürün stok
              dolana kadar 1 iş gününde değil, üretim süresinde gönderilir.
            </p>
          ) : null}
        </div>

        <div>
          <dt className="text-sm text-gray-600">Teslim süresi</dt>
          <dd className="mt-1 text-2xl font-semibold tabular-nums text-gray-900">
            {n(lead, " iş günü")}
          </dd>
          <p className="mt-1 text-xs text-gray-600">
            {product.leadTimeDays == null
              ? "Ayarlardaki varsayılan"
              : "Bu ürüne özel girilmiş"}
          </p>
        </div>

        <div>
          <dt className="text-sm text-gray-600">Politika kaynağı</dt>
          <dd className="mt-1 text-sm text-gray-900">
            {product.stockPolicySource === "shopify"
              ? "Shopify etiketi"
              : product.stockPolicySource === "manual"
                ? "Elle ayarlandı (eski)"
                : "Geçmişten türetildi (eski)"}
          </dd>
          <p className="mt-1 text-xs text-gray-600">
            {product.stockPolicySource === "shopify"
              ? "Shopify’daki ayni-gun-kargo etiketinden geliyor."
              : "Bu ürünün etiketi henüz Shopify’dan tazelenmedi."}
          </p>
        </div>
      </dl>

      <p className="mt-3 rounded border border-gray-200 bg-gray-50 px-3 py-2 text-xs text-gray-700">
        Her üründen sipariş alınabilir. Bu ayar <strong>satışı değil TESLİM
        SÜRESİNİ</strong> belirler: stoğa üretilen ürün stokta varsa hızlı
        gönderilir, stok bittiğinde normal üretim süresine döner.
      </p>

      <div className="mt-4 border-t border-gray-200 pt-3">
        <p className="text-xs text-gray-700">
          Bu ürünü hızlı gönderime almak veya çıkarmak için{" "}
          <strong>Shopify’da ürün etiketini</strong> değiştirin:{" "}
          <code className="rounded bg-gray-100 px-1 py-0.5">ayni-gun-kargo</code>{" "}
          etiketi varsa hızlı gönderim, yoksa sipariş üzerine üretim. Balder her
          ürün güncellemesinde bu etiketi okuyup kendini günceller — buradan elle
          değiştirilse bir sonraki güncellemede geri alınırdı.
        </p>
        <p className="mt-2 text-xs text-gray-600">
          Teslim süreleri: stoktan{" "}
          {n(settings.data?.leadTimeMadeToStock ?? 1, " iş günü")}, sipariş
          üzerine {n(settings.data?.leadTimeMadeToOrder ?? 10, " iş günü")}.
        </p>
      </div>

      {inv.error ? (
        <p role="alert" className="mt-3 text-sm text-red-700">
          Stok bilgisi okunamadı: {inv.error}
        </p>
      ) : null}
    </section>
  );
}

export default StockPanel;
